<template>
    <form v-if="status.detail" @submit.prevent="submitForm">
        <div class="row">
            <div class="col-md-6">
                <b-form-group :label="$t('label.code')">
                    <b-form-input v-model.trim="form.code" required /></b-form-group>
            </div>
            <div class="col-md-6"><b-form-group :label="$t('label.name')">
                    <b-form-input v-model.trim="form.name" required /></b-form-group>
            </div>
        </div>
        <div class="row">
            <div class="col-md-6"><b-form-group :label="$t('label.company')">
                    <multiselect v-model="form.company" track-by="value" label="text" :options="companyDdl"
                        :loading="companyLoading" :placeholder="$t('label.searchSelectCompany')" @input="companyChanged"
                        @search-change="searchCompanies" />
                </b-form-group></div>
            <div class="col-md-6"><b-form-group :label="$t('label.office')">
                    <multiselect v-model="form.office" track-by="value" label="text" :options="officeDdl"
                        :disabled="!form.company" :placeholder="$t('label.selectOffice')" />
                </b-form-group></div>
        </div>
        <div class="row">
            <div class="col-md-6"><b-form-group :label="$t('label.capacity')"><b-form-input
                        v-model.number="form.capacity" type="number" min="1" step="1" required /></b-form-group></div>
            <div class="col-md-6"><b-form-group :label="$t('label.picUser')">
                    <multiselect v-model="form.picUser" track-by="value" label="text" :options="userDdl"
                        :disabled="!form.company" :placeholder="$t('label.searchSelectPicUser')" />
                </b-form-group></div>
        </div>
        <b-form-group :label="$t('label.description')"><b-form-textarea v-model.trim="form.description"
                rows="3" /></b-form-group>
        <b-form-group :label="$t('label.amenities')">
            <multiselect v-model="form.amenities" track-by="value" label="text" :options="amenityDdl" :multiple="true"
                :close-on-select="false" :loading="amenityLoading" :placeholder="$t('label.selectAmenities')"
                @search-change="searchAmenities" />
        </b-form-group>
        <b-form-group :label="$t('label.image')"><b-form-file v-model="form.image" accept="image/*"
                :placeholder="$t('label.chooseImage')" /><b-form-text v-if="meetingRoomUUID && form.imageUrl">{{
                    $t('label.keepCurrentImage') }}</b-form-text></b-form-group>
        <div class="row">
            <div class="col-md-6"><b-form-checkbox v-model="form.isMuslimFriendly" switch>{{ $t('label.muslimFriendly')
            }}</b-form-checkbox></div>
            <div class="col-md-6"><b-form-checkbox v-model="form.isActive" switch>{{ form.isActive ? $t('label.active')
                : $t('label.inactive') }}</b-form-checkbox></div>
        </div>
        <hr>
        <div class="d-flex justify-content-end"><b-button variant="danger" class="mr-2" @click="$emit('done')">{{
            $t('label.cancel') }}</b-button><b-button type="submit" variant="info" :disabled="!status.save"><span
                    v-if="status.save">{{ $t(meetingRoomUUID ? 'label.updateX' : 'label.createX',
                        [$t('module.meetingRoom')]) }}</span><b-spinner v-else small /></b-button></div>
    </form>
    <div v-else class="text-center p-5"><b-spinner variant="info" /></div>
</template>

<script>
import { mapState } from 'vuex';
import Multiselect from 'vue-multiselect';
import { meetingRoomMethods, lookupMethods } from '@/state/api';
import { MEETING_ROOM_MODULE } from '@/state/api/meetingRoom.state';

export default {
    name: 'MeetingRoomForm',
    components: { Multiselect },
    props: {
        meetingRoomUUID: { type: String, default: '' },
        searchCriteria: { type: Object, required: true }
    },
    data() {
        return {
            amenityLoading: false,
            amenityTimer: null,
            companyLoading: false,
            companyTimer: null,
            form: {
                uuid: '',
                code: '',
                name: '',
                company: null,
                office: null,
                capacity: null,
                picUser: null,
                description: '',
                amenities: [], image: null,
                imageUrl: '',
                isMuslimFriendly: false,
                isActive: true
            }
        };
    },
    computed: {
        ...mapState(MEETING_ROOM_MODULE, ['status']),
        companyDdl() {
            return this.$store.state.api.lookup.companyDdl || [];
        },
        officeDdl() {
            return this.$store.state.api.lookup.officeDdl || [];
        },
        userDdl() {
            return this.$store.state.api.lookup.userDdl || [];
        },
        amenityDdl() {
            return this.$store.state.api.lookup.amenityDdl || [];
        }
    },
    async created() {
        const initialLookups = [this.lookupCompany(), this.lookupAmenity()];
        if (this.meetingRoomUUID) initialLookups.push(this.load());
        await Promise.all(initialLookups);
    },
    beforeDestroy() {
        clearTimeout(this.amenityTimer);
        clearTimeout(this.companyTimer);
    },
    methods: {
        ...meetingRoomMethods,
        ...lookupMethods,
        option(options, uuid, record) {
            return options.find((item) => item.value === uuid) || (uuid ? { value: uuid, text: record?.name || uuid } : null);
        },
        async load() {
            if (!this.meetingRoomUUID) return;

            const result = await this.getMeetingRoom({ uuid: this.meetingRoomUUID });

            if (!result) return;

            const details = this.$store.state.api.meetingRoom.details;
            this.form = {
                ...this.form,
                ...details,
                image: null,
                company: this.option(this.companyDdl, details.companyUUID, details.company),
                office: this.option(this.officeDdl, details.officeUUID, details.office),
                picUser: this.option(this.userDdl, details.picUserUUID, details.picUser),
                amenities: details.amenities.map((item) => ({ value: item.uuid, text: item.name }))
            };

            await Promise.all([
                this.lookupOffice({ companyUUID: details.companyUUID }),
                this.lookupUser({ companyUUID: details.companyUUID })
            ]);
        },
        async companyChanged(company) {
            this.form.office = null;
            this.form.picUser = null;
            if (company) {
                await Promise.all([this.lookupOffice({ companyUUID: company.value }), this.lookupUser({ companyUUID: company.value })]);
            }
        },
        searchCompanies(term) {
            clearTimeout(this.companyTimer);
            this.companyTimer = setTimeout(async () => {
                this.companyLoading = true;
                await this.lookupCompany(term ? [term] : []);
                this.companyLoading = false;
            }, 350);
        },
        searchAmenities(term) {
            clearTimeout(this.amenityTimer);
            this.amenityTimer = setTimeout(async () => {
                this.amenityLoading = true;
                await this.lookupAmenity(term ? [term] : []);
                this.amenityLoading = false;
            }, 350);
        },
        async submitForm() {
            if (!this.form.company || !this.form.office || !this.form.picUser) return;

            const data = {
                ...this.form,
                companyUUID: this.form.company.value,
                officeUUID: this.form.office.value,
                picUserUUID: this.form.picUser.value,
                amenityUUIDs: this.form.amenities.map((item) => item.value)
            };

            const action = this.meetingRoomUUID ? this.updateMeetingRoom : this.createMeetingRoom;
            const result = await action({ data, filter: this.searchCriteria }); if (result?.success) this.$emit('done');
        },
    },
};
</script>
